'use client';

import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface HeadingsContextType {
  headings: Heading[];
  setHeadings: React.Dispatch<React.SetStateAction<Heading[]>>;
  visibleHeadingIds: Set<string>;
  setVisibleHeadingIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  expandedNodes: Set<string>;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  meta: any;
  setMeta: React.Dispatch<React.SetStateAction<any>>;
}

const HeadingsContext = createContext<HeadingsContextType | undefined>(undefined);

export function HeadingsProvider({ children }: { children: ReactNode }) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [visibleHeadingIds, setVisibleHeadingIds] = useState<Set<string>>(new Set());
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [meta, setMeta] = useState<any>({});

  // Prune collapsed IDs that no longer exist when headings change
  useEffect(() => {
    setExpandedNodes((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(headings.map((h) => h.id));
      const filtered = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) filtered.add(id);
      });
      return filtered;
    });
  }, [headings]);

  return (
    <HeadingsContext.Provider
      value={{
        headings,
        setHeadings,
        visibleHeadingIds,
        setVisibleHeadingIds,
        expandedNodes,
        setExpandedNodes,
        meta,
        setMeta,
      }}>
      {children}
    </HeadingsContext.Provider>
  );
}

export function useHeadings() {
  const context = useContext(HeadingsContext);
  if (context === undefined) {
    throw new Error('useHeadings must be used within a HeadingsProvider');
  }
  return context;
}
